import { getToken, onMessage } from 'firebase/messaging'
import { doc, updateDoc, arrayUnion } from 'firebase/firestore'
import { messaging as messagingPromise, db } from './firebase'

// Get your VAPID key from:
// Firebase Console → Project Settings → Cloud Messaging → Web Push certificates → Generate key pair
const VAPID_KEY = 'BK1n4Fpyged1gB68M_T3SAjFHgpGjy_fQpgjKb2DkXJZej1QaiBuJWeZnkTX8_SZlFa2kOeU4OPlpr14SKwNx6Y'

/**
 * Requests notification permission, registers the service worker,
 * retrieves the FCM token, and stores it in Firestore users/{userId}.
 *
 * @param {string} userId - Firebase UID of the authenticated user
 * @returns {Promise<string|null>} The FCM token, or null if permission was denied / unsupported
 */
export async function registerForPushNotifications(userId) {
  // Guard: service workers required
  if (!('serviceWorker' in navigator) || !('Notification' in window)) {
    console.warn('Push notifications are not supported in this browser')
    return null
  }

  const messaging = await messagingPromise
  if (!messaging) {
    console.warn('Firebase Messaging is not supported in this browser')
    return null
  }

  // Request permission
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    console.warn('Notification permission denied')
    return null
  }

  // Register the service worker with root scope, then wait until it is fully active
  await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' })
  const registration = await navigator.serviceWorker.ready

  // Get FCM token
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration
  })

  if (!token) {
    console.warn('No FCM token received')
    return null
  }

  // Persist token to Firestore — arrayUnion prevents duplicates across devices
  const userRef = doc(db, 'users', userId)
  await updateDoc(userRef, {
    fcm_tokens: arrayUnion(token)
  })

  return token
}

/**
 * Listens for FCM messages while the app is in the foreground.
 * The browser suppresses system notifications when the app is focused,
 * so you must handle display manually here.
 *
 * @param {Function} callback - Called with the payload when a message arrives
 * @returns {Function} Unsubscribe function
 */
export async function onForegroundMessage(callback) {
  const messaging = await messagingPromise
  if (!messaging) return () => {}
  return onMessage(messaging, callback)
}
